import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";

export default function ConfigPayments() {
  const [values, setValues] = useState({ adminCommission: "", tva: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      setLoading(true);
      const ref = doc(db, "config", "payments");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setValues({
          adminCommission: data.adminCommission ?? "",
          tva: data.tva ?? "",
        });
      }
      setLoading(false);
    }
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, "config", "payments");
      await updateDoc(ref, {
        adminCommission: Number(values.adminCommission),
        tva: Number(values.tva),
      });

      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Configuration enregistrée avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch (e) {
      alert("Erreur lors de l'enregistrement.");
    }
    setSaving(false);
  };

  const exampleAmount = 100;
  const commissionAmount = (exampleAmount * (values.adminCommission || 0)) / 100;
  const tvaAmount = (exampleAmount * (values.tva || 0)) / 100;
  const totalWithTva = commissionAmount + tvaAmount;
  const couturierAmount = exampleAmount - totalWithTva;

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Commission</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{values.adminCommission || "0"}%</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">TVA</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{values.tva || "0"}%</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Form */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Paramètres</span>
              </div>
              <div className="p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Commission Admin <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="adminCommission"
                      value={values.adminCommission}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex: 15"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">Pourcentage prélevé sur chaque transaction</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    TVA <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="tva"
                      value={values.tva}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex: 20"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">TVA appliquée sur le montant total</p>
                </div>

                <div className="flex justify-end">
                  <button
                    disabled={saving || !values.adminCommission || !values.tva}
                    onClick={handleSave}
                    className="px-4 py-1.5 bg-[#801111] text-white rounded-md text-xs font-medium hover:bg-[#5a0c0c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Sauvegarde..." : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Aperçu — transaction de {exampleAmount}€</span>
              </div>
              <div className="p-3 space-y-2">
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded text-xs">
                  <span className="text-gray-700">Montant transaction</span>
                  <span className="font-medium text-gray-900">{exampleAmount.toFixed(2)}€</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-red-50 rounded text-xs">
                  <span className="text-gray-700">
                    Commission {values.adminCommission ? `(${values.adminCommission}%)` : ""}
                  </span>
                  <span className="font-medium text-[#801111]">-{commissionAmount.toFixed(2)}€</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-orange-50 rounded text-xs">
                  <span className="text-gray-700">
                    TVA {values.tva ? `(${values.tva}%)` : ""}
                  </span>
                  <span className="font-medium text-orange-600">-{tvaAmount.toFixed(2)}€</span>
                </div>

                <div className="border-t border-gray-200 my-1"></div>

                <div className="flex justify-between items-center p-2 bg-gray-100 rounded text-xs">
                  <span className="font-medium text-gray-700">Total prélevé</span>
                  <span className="font-semibold text-gray-900">-{totalWithTva.toFixed(2)}€</span>
                </div>

                <div className="flex justify-between items-center p-2 bg-green-50 rounded text-xs border border-green-200">
                  <div>
                    <span className="font-medium text-gray-700 block">Montant couturier</span>
                    <span className="text-[10px] text-gray-500">Net après prélèvements</span>
                  </div>
                  <span className="font-bold text-lg text-green-600">{couturierAmount.toFixed(2)}€</span>
                </div>

                <div className="p-2 bg-blue-50 rounded text-[10px] text-blue-700 mt-2">
                  Commission = Montant × {values.adminCommission || 0}% · TVA = Montant × {values.tva || 0}% · Couturier = Montant - Commission - TVA
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
